import * as React from 'react';
import ReactModal from 'react-modal';

// Styles.
import { HeaderWrapper, IconWrapper, CloseBox } from './Modal.style';

// Components.
import Body from './Body';
import Footer from './Footer';
import Grid from '../layoutComponents/Grid';
import { Info, Warning, Error, Close } from '@mui/icons-material';

/**
 * Mapping of `ModalKind` to icon for Modal header.
 */
const icons = {
  primary: <Info />,
  warning: <Warning />,
  danger: <Error />
};

/**
 * Kinds of modals.
 */
export type ModalKind = 'basic' | 'primary' | 'warning' | 'danger';

 export interface IModalProps extends ReactModal.Props {
  /**
   * Heading of modal.
   */
  heading: string;

  /**
   * Kind of modal to display.
   */
  kind: ModalKind;

  /**
   * Event handler that runs right before the Modal is instructed to close. This
   * is a required prop as the Modal itself does not maintain internal state.
   */
  onRequestClose(event?: React.MouseEvent | React.KeyboardEvent): void;

  /**
   * Aria label for the close button
   */
  closeButtonAriaLabel?: string;
}

export interface IModalComposition {
  Body: React.FC;
  Footer: React.FC;

  /**
   * Set your app's root element to properly hide your application from
   * assistive screen readers and other assistive technologies while the modal is open.
   *
   * More info here: http://reactcommunity.org/react-modal/accessibility/#accessibility
   */
  setAppElement: (appElement: string | HTMLElement | null) => void;
}

/**
 * Modal component.
 */
const Modal: React.FC<IModalProps> & IModalComposition = ({
  kind,
  heading,
  closeButtonAriaLabel,
  children,
  onRequestClose,
  ...rest
}) => {
  if (process.env.NODE_ENV !== 'production') {
    if (!closeButtonAriaLabel) {
      /* eslint-disable-next-line no-console */
      console.warn(
        "Please provide an aria-label for the close button through the 'closeButtonAriaLabel' prop"
      );
    }
  }

  return (
    <ReactModal
      className="lumos-modal"
      overlayClassName="lumos-modal-overlay"
      {...rest}
    >
      <HeaderWrapper kind={kind}>
        <Grid alignItems="center">
          {kind === 'basic' ? null : <IconWrapper>{icons[kind]}</IconWrapper>}
          <span>{heading}</span>
        </Grid>
        <CloseBox
          as="button"
          position="relative"
          backgroundColor="transparent"
          alignItems="center"
          justifyContent="center"
          color="grayScale.11"
          onClick={() => onRequestClose()}
        >
          <Close />
        </CloseBox>
      </HeaderWrapper>
      {children}
    </ReactModal>
  );
};

// Compose.
Modal.Body = Body;
Modal.Footer = Footer;
Modal.setAppElement = ReactModal.setAppElement;

// Set display names.
Modal.Body.displayName = 'Modal.Body';
Modal.Footer.displayName = 'Modal.Footer';

export default Modal;
